import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import useLocalStorage from 'react-use-localstorage';
import tinycolor from 'tinycolor2';

import { CheckmarkStroke } from 'src/svg/checkmark-stroke';

/**
 * A much smaller drop-in alternative to `ColorPickerV2` for forms with many
 * color fields packed close together (e.g. Theme's Mobile App section) where
 * the full-size picker's card chrome (shadow, border, 53px header row, large
 * swatches) takes up more room than the field warrants.
 *
 * Same prop contract as `ColorPickerV2` so it can be swapped in without
 * touching the surrounding <Controller>/react-hook-form wiring.
 *
 * The popover is rendered into a `document.body` portal, positioned via the
 * trigger's own `getBoundingClientRect()`, rather than as a normal
 * `position: absolute` child of the trigger. This is what guarantees opening
 * it never resizes/reflows whatever grid cell or column the trigger lives in
 * — the popover's DOM node isn't a descendant of that layout at all, so it
 * structurally can't influence it (also sidesteps it getting clipped by any
 * ancestor's `overflow: hidden`, e.g. the sticky preview column).
 *
 * Interaction model (every selection — preset swatch, previously-used custom
 * color, or the hex picker — follows the same live-preview-then-commit flow):
 *  - Opening the popover snapshots the current value.
 *  - Clicking any swatch, or dragging the hex picker, calls `onSelect`
 *    immediately so anything else reading this field (the live preview, a
 *    duplicated "shared" control elsewhere on the page, etc.) updates in
 *    real time — but the popover stays open and nothing is persisted to
 *    local storage yet.
 *  - Clicking "Set" commits: it closes the popover and, for a custom hex
 *    value, adds it to the small locally-stored "previously used" list.
 *  - Dismissing the popover any other way (clicking outside, Escape) without
 *    clicking Set reverts — it calls `onSelect` with the snapshotted value
 *    from when the popover opened.
 */
type ColorPickerCompactProps = {
  colors: string[];
  label: string;
  onSelect: (color: string) => void;
  selectedColor: string;
  allowCustomColorSelection?: boolean;
  helperText?: string;
};

const MAX_CUSTOM_COLORS = 24;
const POPOVER_WIDTH = 240;
const POPOVER_GAP = 4;

type PopoverPosition = {
  top: number;
  left: number;
};

export function ColorPickerCompact({
  colors,
  label,
  onSelect,
  selectedColor,
  allowCustomColorSelection = true,
  helperText,
}: ColorPickerCompactProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(selectedColor || '');
  const [customColors, setCustomColors] = useLocalStorage('customColors');
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const customColorOptions: string[] = JSON.parse(customColors || '[]');
  const valueAtOpenRef = useRef(selectedColor);
  // `revertAndClose` is called from the mousedown/keydown listeners registered by the effect
  // below, which only re-runs when `isOpen` changes -- so it closes over whatever `customColor`
  // was *at the time the popover opened*, not whatever it is by the time the user actually
  // dismisses it. A ref (always current, unlike state captured in a stale closure) is what makes
  // the "did anything actually change during this session" check below correct.
  const customColorRef = useRef(customColor);
  customColorRef.current = customColor;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePopoverPosition = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    // Anchored via `position: fixed` (viewport-relative), so the raw rect coordinates -- already
    // viewport-relative -- are used directly, with no scrollX/scrollY offset needed. Flips to the
    // right edge of the trigger, rather than the left, if it would otherwise overflow off-screen.
    const overflowsRight = rect.left + POPOVER_WIDTH > window.innerWidth;

    setPopoverPosition({
      top: rect.bottom + POPOVER_GAP,
      left: overflowsRight ? rect.right - POPOVER_WIDTH : rect.left,
    });
  };

  const openPopover = (): void => {
    valueAtOpenRef.current = selectedColor;
    setCustomColor(selectedColor || '');
    updatePopoverPosition();
    setIsOpen(true);
  };

  const revertAndClose = (): void => {
    // Only actually revert (call onSelect) if a preview selection during this session changed
    // the value away from what it was when the popover opened. Calling onSelect unconditionally
    // here -- even with the exact same value the field already has -- used to mark the
    // surrounding react-hook-form field dirty regardless: react-hook-form v6 doesn't skip
    // `onChange` calls that pass an unchanged value, so simply opening a picker and dismissing
    // it without picking anything (e.g. Escape, clicking away) was enough to trip the "You have
    // unpublished changes" prompt on navigating away, even though nothing was ever edited.
    if (customColorRef.current !== valueAtOpenRef.current) {
      onSelect(valueAtOpenRef.current);
    }

    setIsOpen(false);
  };

  const commitAndClose = (): void => {
    const normalized = customColor.toLowerCase();
    const isKnownColor = colors.includes(normalized) || customColorOptions.includes(normalized);

    if (tinycolor(normalized).isValid() && !isKnownColor) {
      const nextCustomColors = [...customColorOptions, normalized].slice(-MAX_CUSTOM_COLORS);
      setCustomColors(JSON.stringify(nextCustomColors));
    }

    setIsOpen(false);
  };

  // Live-preview a selection (swatch click, previously-used swatch, or hex-picker drag) without
  // committing yet — react-colorful's HexColorPicker only ever emits complete, valid colors
  // (during drag), so it's always safe to forward straight to onSelect here.
  const previewSelect = (color: string): void => {
    setCustomColor(color);
    onSelect(color);
  };

  // The hex *text* input, unlike the picker, emits every keystroke -- including incomplete
  // strings while typing ("#1", "#12", ...). Keep the field's own text in sync always, but only
  // forward to onSelect (and thus the live preview) once it's a real color.
  const handleHexInputChange = (value: string): void => {
    setCustomColor(value);

    if (tinycolor(value).isValid()) {
      onSelect(value);
    }
  };

  // Dismiss-without-Set (click outside, Escape) reverts to the pre-open value. Checks both the
  // trigger and the (portaled, so *not* a DOM descendant of the trigger) popover.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedPopover = popoverRef.current?.contains(target);

      if (!clickedTrigger && !clickedPopover) {
        revertAndClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        revertAndClose();
      }
    };

    const handleReposition = (): void => updatePopoverPosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revertAndClose closes over selectedColor/valueAtOpenRef intentionally re-created each render
  }, [isOpen]);

  return (
    <>
      <Row ref={triggerRef} onClick={() => (isOpen ? revertAndClose() : openPopover())} type='button'>
        <SmallSwatch $color={selectedColor} />
        <SmallLabel>{label}</SmallLabel>
        <Chevron $isOpen={isOpen} />
      </Row>

      {isOpen &&
        popoverPosition &&
        createPortal(
          <Popover ref={popoverRef} $top={popoverPosition.top} $left={popoverPosition.left}>
            {helperText && <HelperText>{helperText}</HelperText>}

            <SwatchGrid>
              {colors.map((color) => (
                <SwatchButton
                  key={color}
                  type='button'
                  $color={color}
                  $isSelected={color === customColor}
                  onClick={() => previewSelect(color)}
                  title={color}
                  aria-label={color}
                >
                  {color === customColor && <StyledCheckmarkStroke />}
                </SwatchButton>
              ))}
            </SwatchGrid>

            {customColorOptions.length > 0 && (
              <>
                <SectionLabel>Previously used</SectionLabel>
                <SwatchGrid>
                  {customColorOptions
                    .slice()
                    .reverse()
                    .map((color) => (
                      <SwatchButton
                        key={color}
                        type='button'
                        $color={color}
                        $isSelected={color === customColor}
                        onClick={() => previewSelect(color)}
                        title={color}
                        aria-label={color}
                      >
                        {color === customColor && <StyledCheckmarkStroke />}
                      </SwatchButton>
                    ))}
                </SwatchGrid>
              </>
            )}

            {allowCustomColorSelection && (
              <CustomColorRow>
                <MiniHexPicker color={customColor || '#ffffff'} onChange={previewSelect} />
                <HexInputRow>
                  <HexColorInput
                    className='compact-hex-input'
                    color={customColor}
                    onChange={handleHexInputChange}
                    prefixed
                  />
                </HexInputRow>
              </CustomColorRow>
            )}

            <PopoverFooter>
              <CancelButton type='button' onClick={revertAndClose}>
                Cancel
              </CancelButton>
              <SetButton type='button' onClick={commitAndClose} disabled={!tinycolor(customColor).isValid()}>
                Set
              </SetButton>
            </PopoverFooter>
          </Popover>,
          document.body
        )}
    </>
  );
}

const Row = styled.button`
  align-items: center;
  appearance: none;
  background: #fff;
  border: 1px solid #d3d8de;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  gap: 10px;
  padding: 12px 14px;
`;

const SmallLabel = styled.span`
  color: #4f5d68;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SmallSwatch = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color || '#fff'};
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px #00000033;
  display: inline-block;
  flex-shrink: 0;
  height: 16px;
  width: 16px;
`;

const Chevron = styled.span<{ $isOpen: boolean }>`
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #8b95a1;
  flex-shrink: 0;
  height: 0;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'none')};
  width: 0;
`;

const Popover = styled.div<{ $top: number; $left: number }>`
  background: #fff;
  border: 1px solid #d3d8de;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  left: ${({ $left }) => $left}px;
  padding: 10px;
  position: fixed;
  top: ${({ $top }) => $top}px;
  width: ${POPOVER_WIDTH}px;
  z-index: 1300;
`;

const HelperText = styled.p`
  color: #6d747b;
  font-size: 11px;
  line-height: 1.4;
  margin: 0 0 6px;
`;

const SectionLabel = styled.p`
  color: #8b95a1;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin: 8px 0 4px;
  text-transform: uppercase;
`;

const SwatchGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const SwatchButton = styled.button<{ $color: string; $isSelected?: boolean }>`
  align-items: center;
  appearance: none;
  background: ${({ $color }) => $color};
  border: none;
  border-radius: 50%;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? '0 0 0 1px #00000099, inset 0 0 0 1px #00000099' : 'inset 0 0 0 1px #00000033'};
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 16px;
  justify-content: center;
  padding: 0;
  width: 16px;
`;

const StyledCheckmarkStroke = styled(CheckmarkStroke)`
  height: 8px;
  width: 8px;
  path {
    stroke: #00000099;
  }
`;

const CustomColorRow = styled.div`
  border-top: 1px solid #e4e8ec;
  margin-top: 8px;
  padding-top: 8px;
`;

const MiniHexPicker = styled(HexColorPicker)`
  height: 90px !important;
  width: 100% !important;
`;

const HexInputRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;

  .compact-hex-input {
    border: 1px solid #d3d8de;
    border-radius: 4px;
    flex: 1;
    font-size: 12px;
    min-width: 0;
    padding: 4px 6px;
  }
`;

const PopoverFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 10px;
`;

const CancelButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: #6d747b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
`;

const SetButton = styled.button`
  appearance: none;
  background: #0b99e6;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
